import api from "./api";

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  STAFF: "staff",
  CUSTOMER: "customer",
};

export const PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: ["*"],
  [ROLES.STAFF]: [
    "view_orders",
    "update_order_status",
    "view_customers",
    "manage_products",
    "view_reports",
  ],
  [ROLES.CUSTOMER]: [
    "view_products",
    "purchase",
    "view_my_orders",
  ],
};

export const generateToken = () => {
  return Date.now() + "_" + Math.random().toString(36).substring(2, 15);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]{11}$/;
  return phoneRegex.test(String(phone).trim());
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(String(email).trim());
};

export const validatePassword = (password) => {
  return String(password).length >= 8;
};

export const normalizeUser = (user) => {
  return {
    ...user,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phone: user.phone || "",
    email: user.email || "",
    role: user.role || ROLES.CUSTOMER,
    permissions:
      user.permissions ||
      PERMISSIONS[user.role] ||
      PERMISSIONS[ROLES.CUSTOMER],
    addresses: Array.isArray(user.addresses) ? user.addresses : [],
    orders: Array.isArray(user.orders) ? user.orders : [],
    createdAt: user.createdAt || new Date().toISOString(),
    updatedAt: user.updatedAt || new Date().toISOString(),
  };
};

export const sanitizeUser = (user) => {
  if (!user) return null;

  const { password, resetToken, resetTokenExpiry, ...safeUser } = user;
  return safeUser;
};

export const getUsers = async () => {
  const response = await api.get("/users");
  return response.data.map(normalizeUser);
};

export const findUserByIdentifier = async (identifier) => {
  const cleanIdentifier = String(identifier).trim();
  const users = await getUsers();

  return users.find((u) => {
    return (
      String(u.phone).trim() === cleanIdentifier ||
      String(u.email).trim().toLowerCase() === cleanIdentifier.toLowerCase()
    );
  });
};

export const findUserByPhoneOrEmail = async (phone, email) => {
  const cleanPhone = String(phone).trim();
  const cleanEmail = String(email).trim().toLowerCase();

  const users = await getUsers();

  return users.find((u) => {
    return (
      String(u.phone).trim() === cleanPhone ||
      String(u.email).trim().toLowerCase() === cleanEmail
    );
  });
};

export const loginUser = async (identifier, password) => {
  const cleanIdentifier = String(identifier).trim();
  const cleanPassword = String(password).trim();

  if (!cleanIdentifier) {
    return {
      success: false,
      message: "لطفاً شماره موبایل یا ایمیل را وارد کنید",
    };
  }

  if (!cleanPassword) {
    return {
      success: false,
      message: "لطفاً رمز عبور را وارد کنید",
    };
  }

  try {
    let loginEmail = cleanIdentifier.toLowerCase();

    /*
      json-server-auth ورود را با email انجام می‌دهد.
      اگر کاربر شماره موبایل وارد کند، اول کاربر را از users پیدا می‌کنیم،
      بعد ایمیل همان کاربر را برای login استفاده می‌کنیم.
    */
    if (!validateEmail(cleanIdentifier)) {
      const foundUser = await findUserByIdentifier(cleanIdentifier);

      if (!foundUser?.email) {
        return {
          success: false,
          message: "کاربری با این اطلاعات یافت نشد",
        };
      }

      loginEmail = String(foundUser.email).trim().toLowerCase();
    }

    const response = await api.post("/auth/login", {
      identifier: loginEmail,
      password: cleanPassword,
    });

    const authData = response.data || {};
    const accessToken = authData.accessToken || authData.token;
    const rawUser = authData.user;

    if (!accessToken || !rawUser) {
      return {
        success: false,
        message: "پاسخ ورود از سرور کامل نیست",
      };
    }

    const safeUser = sanitizeUser(normalizeUser(rawUser));

    return {
      success: true,
      message: "ورود با موفقیت انجام شد",
      user: safeUser,
      token: accessToken,
      role: safeUser.role,
    };
  } catch (error) {
    console.error("Login API error:", error);

    if (error.response?.status === 400 || error.response?.status === 401) {
      return {
        success: false,
        message: "ایمیل/شماره موبایل یا رمز عبور اشتباه است",
      };
    }

    return {
      success: false,
      message: "خطا در ورود. لطفاً اتصال به دیتابیس را بررسی کنید.",
    };
  }
};

export const registerUser = async ({ username, phone, email, password }) => {
  const cleanUsername = String(username).trim();
  const cleanPhone = String(phone).trim();
  const cleanEmail = String(email).trim().toLowerCase();
  const cleanPassword = String(password).trim();

  if (!cleanUsername) {
    return {
      success: false,
      message: "لطفاً نام کاربری را وارد کنید",
    };
  }

  if (!validatePhone(cleanPhone)) {
    return {
      success: false,
      message: "شماره موبایل باید 11 رقم باشد",
    };
  }

  if (!validateEmail(cleanEmail)) {
    return {
      success: false,
      message: "ایمیل معتبر نیست",
    };
  }

  if (!validatePassword(cleanPassword)) {
    return {
      success: false,
      message: "رمز عبور باید حداقل 8 کاراکتر باشد",
    };
  }

  try {
    const existingUser = await findUserByPhoneOrEmail(cleanPhone, cleanEmail);

    if (existingUser) {
      return {
        success: false,
        message: "این شماره یا ایمیل قبلاً ثبت شده است",
      };
    }
  } catch (error) {
    console.warn("Duplicate user check failed, continuing with /register:", error);
  }

  const now = new Date().toISOString();

  const newUser = {
    username: cleanUsername,
    firstName: "",
    lastName: "",
    phone: cleanPhone,
    email: cleanEmail,
    password: cleanPassword,
    role: ROLES.CUSTOMER,
    permissions: PERMISSIONS[ROLES.CUSTOMER],
    addresses: [],
    orders: [],
    createdAt: now,
    updatedAt: now,
    createdBy: "self-register",
  };

  try {
    const response = await api.post("/auth/register", newUser);

    const authData = response.data || {};
    const rawUser = authData.user || authData;

    return {
      success: true,
      message: "ثبت‌نام با موفقیت انجام شد",
      user: sanitizeUser(normalizeUser(rawUser)),
      token: authData.accessToken || authData.token || null,
    };
  } catch (error) {
    console.error("Register API error:", error);

    if ([400, 409].includes(error.response?.status)) {
      return {
        success: false,
        message: error.response?.data?.message || "این شماره یا ایمیل قبلاً ثبت شده است یا اطلاعات واردشده معتبر نیست",
      };
    }

    return {
      success: false,
      message: "خطا در ثبت‌نام. لطفاً اتصال به دیتابیس را بررسی کنید.",
    };
  }
};

export const createStaffUser = async ({
  username,
  phone,
  email,
  password,
  createdBy,
}) => {
  const cleanUsername = String(username).trim();
  const cleanPhone = String(phone).trim();
  const cleanEmail = String(email).trim().toLowerCase();
  const cleanPassword = String(password).trim();

  if (!cleanUsername) {
    return {
      success: false,
      message: "لطفاً نام کاربری را وارد کنید",
    };
  }

  if (!validatePhone(cleanPhone)) {
    return {
      success: false,
      message: "شماره موبایل باید 11 رقم باشد",
    };
  }

  if (!validateEmail(cleanEmail)) {
    return {
      success: false,
      message: "ایمیل معتبر نیست",
    };
  }

  if (!validatePassword(cleanPassword)) {
    return {
      success: false,
      message: "رمز عبور باید حداقل 8 کاراکتر باشد",
    };
  }

  const existingUser = await findUserByPhoneOrEmail(cleanPhone, cleanEmail);

  if (existingUser) {
    return {
      success: false,
      message: "این شماره یا ایمیل قبلاً ثبت شده است",
    };
  }

  const now = new Date().toISOString();

  const newStaff = {
    username: cleanUsername,
    firstName: "",
    lastName: "",
    phone: cleanPhone,
    email: cleanEmail,
    password: cleanPassword,
    role: ROLES.STAFF,
    permissions: PERMISSIONS[ROLES.STAFF],
    addresses: [],
    orders: [],
    createdAt: now,
    updatedAt: now,
    createdBy: createdBy || "system",
  };

  const response = await api.post("/users", newStaff);

  return {
    success: true,
    message: "کارمند جدید با موفقیت اضافه شد",
    staff: sanitizeUser(normalizeUser(response.data)),
  };
};

export const deleteUserById = async (userId) => {
  const users = await getUsers();
  const userToDelete = users.find((u) => String(u.id) === String(userId));

  if (!userToDelete) {
    return {
      success: false,
      message: "کاربر مورد نظر یافت نشد",
    };
  }

  if (userToDelete.role === ROLES.SUPER_ADMIN) {
    return {
      success: false,
      message: "امکان حذف ادمین اصلی وجود ندارد",
    };
  }

  await api.delete(`/users/${userId}`);

  return {
    success: true,
    message: "کاربر با موفقیت حذف شد",
  };
};

export const updateUserById = async (userId, updates) => {
  const response = await api.patch(`/users/${userId}`, {
    ...updates,
    updatedAt: new Date().toISOString(),
  });

  return sanitizeUser(normalizeUser(response.data));
};

export const forgotPasswordUser = async (identifier) => {
  const response = await api.post("/password-recovery/request", { identifier: String(identifier).trim() });
  return { success: true, ...response.data };
};

export const getCurrentSessionUser = async () => {
  const response = await api.get("/auth/me");
  return normalizeUser(response.data.user);
};

export const resetPasswordUser = async (resetToken, newPassword) => {
  if (!validatePassword(newPassword)) {
    return {
      success: false,
      message: "رمز عبور باید حداقل 8 کاراکتر باشد",
    };
  }

  const response = await api.post("/password-recovery/reset", { token: resetToken, password: String(newPassword).trim() });
  return { success: true, ...response.data };
};
